/**
 * Ridership by Stop – region grouping for dynamic routes (VSER-14220).
 *
 * Dynamic (flexiRide-style) route groups get a different set of stops every
 * day, so one bar per stop says nothing (Intuit: ~780 stops). The BE
 * (VSER-14219) keeps `GET /ridership/stops` stop-based but stamps every row
 * with the region the stop falls in (`regionId` / `regionName`); the region is
 * resolved from the workgroup template's region POIs when an optimisation is
 * imported, so it is only populated for stops of DYNAMIC workgroups whose
 * template defines regions. Destination rows and stops of templates without
 * regions come back with `regionId: null`.
 *
 * Agreed FE rule:
 *  - `regionId != null` → rows with the same `regionId` collapse into ONE row
 *    labelled with `regionName`; `totalOn`/`totalOff` are summed and `routes`
 *    are merged by `routeId` (same route → totals summed).
 *  - `regionId == null` → the row is passed through untouched (as-is, per stop).
 *
 * Both kinds can appear in the same response. The grouped row keeps the
 * `RidershipStopDto` shape (so the chart code doesn't care which kind it gets)
 * plus `isRegion`, `stopIds` and `stopNames` describing its members.
 */

const num = (value) => (Number.isFinite(value) ? value : 0);

function routeKey(route) {
  return route.routeId != null ? `id:${route.routeId}` : `name:${route.routeName}`;
}

function mergeRoutes(routeAgg, routes) {
  (routes || []).forEach((route) => {
    if (!route) return;
    const key = routeKey(route);
    const existing = routeAgg.get(key);
    if (existing) {
      existing.totalOn += num(route.totalOn);
      existing.totalOff += num(route.totalOff);
      if (existing.workingType == null && route.workingType != null) existing.workingType = route.workingType;
      return;
    }
    routeAgg.set(key, {
      routeId: route.routeId ?? null,
      routeName: route.routeName,
      totalOn: num(route.totalOn),
      totalOff: num(route.totalOff),
      workingType: route.workingType ?? null,
    });
  });
}

/**
 * @param {Array<Object>} rawStops – `GET /ridership/stops` response rows
 * @returns {Array<Object>} rows to chart: region rows (grouped) + plain stop rows (as-is),
 *   in first-appearance order. The caller is expected to sort.
 */
export default function groupStopsByRegion(rawStops) {
  if (!Array.isArray(rawStops) || rawStops.length === 0) return [];

  const result = [];
  const groups = new Map(); // regionId -> { row, routeAgg }

  rawStops.forEach((stop) => {
    if (!stop) return;

    if (stop.regionId == null) {
      result.push(stop);
      return;
    }

    let group = groups.get(stop.regionId);
    if (!group) {
      const regionName = typeof stop.regionName === 'string' ? stop.regionName.trim() : '';
      group = {
        row: {
          stopId: null,
          // Bars are labelled by the region. Fall back to the stop's own name
          // rather than an empty label if the region POI has no name.
          stopName: regionName || stop.stopName,
          regionId: stop.regionId,
          regionName: regionName || null,
          workingType: stop.workingType ?? null,
          isRegion: true,
          stopIds: [],
          stopNames: [],
          totalOn: 0,
          totalOff: 0,
          routes: [],
        },
        routeAgg: new Map(),
      };
      groups.set(stop.regionId, group);
      result.push(group.row);
    }

    const { row, routeAgg } = group;
    if (stop.stopId != null) row.stopIds.push(stop.stopId);
    if (stop.stopName) row.stopNames.push(stop.stopName);
    if (row.workingType == null && stop.workingType != null) row.workingType = stop.workingType;
    row.totalOn += num(stop.totalOn);
    row.totalOff += num(stop.totalOff);
    mergeRoutes(routeAgg, stop.routes);
  });

  groups.forEach((group) => {
    const { row, routeAgg } = group;
    row.routes = Array.from(routeAgg.values());
  });

  return result;
}
